import { ThunkDispatch } from '@reduxjs/toolkit';
import React, { createRef, RefObject } from 'react';
import { navigate } from '@reach/router';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import { ICompany, IProject, TProjects } from 'store/companies/companiesTypes';
import { ITasks, IUsers, RootState } from 'store/store';
import { ISearchParams } from 'store/entity/types';
import { Header, ListOptions } from './styles';
import { black } from 'utils/style/colors';
import messages from './Messages';
import TaskReport from 'components/Report/TaskReport/TaskReport';
import SubtaskList from './SubtaskList';
import TemplateSelectorList from 'containers/Tasks/TasksManagement/TemplateSelectorList';
import { IRoleOption, IUserOption } from 'components/UserSelector/UserSelector';
import { copyTaskListToClipboard } from './TaskUtils';
import { TaskOptionsIcon } from 'components/TaskEditorHeader/styles';
import { appDispatch } from 'store/store';
import APopover from 'components/APopover';
import Button from 'components/Button';
import {
  searchTasks,
  searchTasksFn,
  updateTask,
  updateTaskFn,
} from 'store/tasks/tasksSlice';
import { ITask } from 'store/tasks/tasksType';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { WindowSize } from '../../../components/TaskListView/TaskListView';
import i18n from '../../../i18n';

interface IStateToProps {
  tasks: ITasks;
  projects: TProjects;
  thisUser: IThisUserState;
  users: IUsers['map'];
  companyOwnerId: number;
  company: ICompany;
}

interface IDispatchToProps {
  searchTasks: (params: ISearchParams) => ReturnType<typeof searchTasksFn>;
  updateTask: (task: ITask) => ReturnType<typeof updateTaskFn>;
}

interface IOwnProps {
  visible: boolean; // workaround to fetch subtasks
  task: ITask;
  templates: ITasks;
  handleClickOnTask: any;
  canEditSubtasks: boolean;
  onUpdateAssignment?: any;
  onUpdateTask: (task: ITask) => void;
  companyId?: number;
  selectedSubTaskId?: number;
  project?: IProject;
  members: { [key: number]: IUserOption | IRoleOption };
  title: string;
  windowSize: WindowSize;
  isDraft?: boolean;
}

type IComponentProps = IOwnProps & IStateToProps & IDispatchToProps;

interface IComponentStates {
  allTasksFetched: boolean;
  from: number;
  loading: boolean;
  selectedTemplate: string | number;
  showReport: boolean;
  fieldValues: { [key: string]: string };
  showProgress: boolean;
  showWeight: boolean;
}

class SubtaskListContainer extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  state = {
    allTasksFetched: false,
    from: 0,
    loading: true,
    selectedTemplate: 'all' as number | 'all',
    showReport: false,
    showProgress: false,
    showWeight: false,
    fieldValues: {},
  };

  includeCompleted: RefObject<HTMLInputElement> = createRef();
  includeStarted: RefObject<HTMLInputElement> = createRef();
  includeNotStarted: RefObject<HTMLInputElement> = createRef();

  private fetchPageSize = 100;

  componentDidUpdate(prevProps: Readonly<IComponentProps>): void {
    this.setState({ from: 0 });
    if (this.props.task.id !== prevProps.task.id) this.searchTasks();
  }

  componentDidMount() {
    this.searchTasks();
  }
  searchTasks = () => {
    const params: ISearchParams = {
      projectId: this.props.task.projectId,
      companyId: this.props.companyId,
      parentId: this.props.task.id,
      from: this.state.from,
      count: this.fetchPageSize,
      order: 'creationDate_asc',
      tt: this.props.task.viewToken,
    };
    this.props
      .searchTasks(params)
      .then(() => {
        this.setState({ loading: false, from: this.fetchPageSize });
      })
      .catch(() => {
        this.setState({ loading: false });
        // display(err);
      });
  };

  handleChangeTemplate =
    (templateId: string | number, fieldValues: { [key: string]: string }) =>
    () => {
      this.setState({ selectedTemplate: templateId, fieldValues });
    };

  handleChangeView = (e: React.MouseEvent) => {
    e.preventDefault();
    this.setState((state) => ({
      showReport: !state.showReport,
    }));
  };

  handleShowProgress = (e: React.MouseEvent) => {
    e.preventDefault();
    this.setState((state) => ({
      showProgress: !state.showProgress,
    }));
  };
  handleCopyTaskList = (e: React.MouseEvent, filteredTasks: Array<ITask>) => {
    e.preventDefault();
    this.props.companyId &&
      copyTaskListToClipboard(
        filteredTasks,
        this.props.users,
        this.props.companyId,
        this.includeCompleted!.current!.checked,
        this.includeStarted!.current!.checked,
        this.includeNotStarted!.current!.checked,
        this.props.task.subTasksTitle + ' ' + this.props.task.title
      );
  };

  handleShowWeight = (e: React.MouseEvent) => {
    e.preventDefault();
    this.setState((state) => ({
      showWeight: !state.showWeight,
    }));
  };

  handlePrintTasksList = (filteredTasksIds: Array<number>) => {
    if (filteredTasksIds.length === 0) return;
    const isSimple = !this.props.tasks.data[filteredTasksIds[0]].printTemplate;

    if (isSimple) {
      let params = `projectId=${this.props.task.projectId}&parentId=${this.props.task.id}&basedOnTemplate=${this.state.selectedTemplate}`;
      const { fieldValues } = this.state;
      if (Object.keys(fieldValues).length) {
        params = `${params}&fieldValues=${JSON.stringify(fieldValues)}`;
      }
      window.open(`/t/${this.props.companyId}/print/tasks?${params}`);
    } else {
      window.open(
        `/t/${this.props.companyId}/tasks/print/${this.props.task.id}/${filteredTasksIds.join(',')}`
      );
    }
  };

  handleAddSubTask = (templateId: number | string) => {
    const parentId = this.props.task.id;
    const projectId = this.props.task.projectId;
    const companyId = this.props.companyId;
    const projectPart = window.location.href.includes('/project/')
      ? `project/${projectId}/`
      : '';
    navigate(
      `/t/${companyId}/${projectPart}task/editor/new/info?template=${templateId}&parent=${parentId}`
    );
  };

  handleRequireTasks = () => {
    const params: ISearchParams = {
      companyId: this.props.companyId,
      projectId: this.props.project && Number(this.props.project.id),
      parentId: this.props.task.id,
      from: this.state.from,
      order: 'creationDate_asc',
    };
    return this.props
      .searchTasks(params)
      .then(({ tasks }) => {
        const allTasksFetched = tasks.length === 0;
        this.setState((prevState) => ({
          from: prevState.from + this.fetchPageSize,
          allTasksFetched,
        }));
        return Promise.resolve();
      })
      .catch(() => Promise.reject());
  };

  render() {
    if (!this.props.visible) {
      return <></>;
    }
    // if (this.state.loading) {
    //   return <LoadingCircleIndicator />;
    // }

    const {
      task,
      tasks,
      thisUser,
      companyId,
      project,
      projects,
      templates,
      onUpdateTask,
      companyOwnerId,
      canEditSubtasks,
      handleClickOnTask,
      onUpdateAssignment,
      members,
      updateTask,
      company,
      title,
      windowSize,
    } = this.props;

    const templateList = Object.values(templates.data).reduce(
      (list: any, templ) => {
        list.push({
          id: templ.id,
          title: templ.taskName,
          fields: templ.fields,
        });
        return list;
      },
      []
    );
    let filteredTasks = Object.values(tasks.data).filter(
      (t) => t.parentId === task.id /* && !t.archived*/
    );
    const allSubtasks = filteredTasks;
    if (this.state.selectedTemplate !== 'all') {
      filteredTasks = filteredTasks.filter(
        (t) => t.templateId.toString() === this.state.selectedTemplate
      );
    }
    filteredTasks = filteredTasks.sort((a: ITask, b: ITask) => {
      let aDate = a.creationDate;
      let bDate = b.creationDate;
      if (aDate === undefined) {
        aDate = 77777777777000;
      }
      if (bDate === undefined) {
        bDate = 77777777777000;
      }
      if (aDate === bDate) {
        return b.id - a.id;
      }
      return aDate - bDate;
    });

    return (
      <div style={{ display: 'grid' }} className="p-4">
        {filteredTasks ? (
          <React.Fragment>
            <Header>
              <div
                style={{
                  margin: '10px 2px',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  color: black,
                }}
              >
                {title}
              </div>
              <ListOptions>
                <TaskOptionsIcon
                  type="printer"
                  title={i18n.t('printIconTitle', { ns: 'taskManager' })}
                  onClick={() =>
                    this.handlePrintTasksList(
                      filteredTasks.map((itm) => itm.id)
                    )
                  }
                />
                {/*<TaskOptionsIcon
                  type={this.state.showProgress ? 'eye-off' : 'eye'}
                  title={
                    this.state.showProgress
                      ? messages.hideIconTitle
                      : messages.showIconTitle
                  }
                  onClick={this.handleShowProgress}
                />*/}
                {/*<TaskOptionsIcon
                  type={this.state.showReport ? 'list' : 'activity'}
                  title={
                    this.state.showReport
                      ? messages.tasksListIconTitle
                      : messages.reportIconTitle
                  }
                  onClick={this.handleChangeView}
                />*/}
                {
                  <APopover
                    arrow
                    curveBorder
                    placement="bottom-start"
                    trigger="click"
                    target={
                      <a href="#!" style={{ color: black }}>
                        <TaskOptionsIcon
                          type="copy"
                          title={i18n.t('copyTaskList', { ns: 'taskManager' })}
                        />
                      </a>
                    }
                  >
                    <div>
                      <label>
                        {
                          i18n.t('copyTaskList', {
                            ns: 'taskManager',
                          }) as string
                        }
                      </label>
                      <p className="switch">
                        <>
                          <label>
                            <input
                              name="includeCompleted"
                              id="includeCompleted"
                              type="checkbox"
                              defaultChecked={true}
                              ref={this.includeCompleted}
                            />
                            <span
                              className="lever"
                              style={{
                                marginInlineStart: '0',
                                marginInlineEnd: '10px',
                              }}
                            />
                          </label>
                          {i18n.t('complete', { ns: 'taskManager' })}
                        </>
                      </p>
                      <p className="switch">
                        <>
                          <label>
                            <input
                              name="includeStarted"
                              id="includeStarted"
                              type="checkbox"
                              defaultChecked={true}
                              ref={this.includeStarted}
                            />
                            <span
                              className="lever"
                              style={{
                                marginInlineStart: '0',
                                marginInlineEnd: '10px',
                              }}
                            />
                          </label>
                          {i18n.t('start', { ns: 'taskManager' })}
                        </>
                      </p>
                      <p className="switch">
                        <>
                          <label>
                            <input
                              name="includeNotStarted"
                              id="includeNotStarted"
                              type="checkbox"
                              defaultChecked={true}
                              ref={this.includeNotStarted}
                            />
                            <span
                              className="lever"
                              style={{
                                marginInlineStart: '0',
                                marginInlineEnd: '10px',
                              }}
                            />
                          </label>
                          {i18n.t('notStarted', { ns: 'taskManager' })}
                        </>
                      </p>
                      <Button
                        onClick={(e) =>
                          this.handleCopyTaskList(e, filteredTasks)
                        }
                        color="green"
                      >
                        {
                          i18n.t('copyTaskList', {
                            ns: 'taskManager',
                          }) as string
                        }
                      </Button>
                    </div>
                  </APopover>
                }
              </ListOptions>
            </Header>
            {templateList.length > 1 && (
              <TemplateSelectorList
                projectId={(project && project.id) || task.projectId}
                company={company}
                tasks={tasks.data}
                projects={projects}
                showCategorized={false}
                showAll={!this.state.showReport}
                onSelect={this.handleChangeTemplate}
                selectedTemplate={this.state.selectedTemplate}
                fieldValues={this.state.fieldValues}
              />
            )}
            {this.state.showReport ? (
              <TaskReport
                companyId={Number(companyId)}
                compactView={true}
                activeProjectId={project ? Number(project.id) : undefined}
                handleChangeProjectId={() => ({})}
                templateId={
                  templateList.length === 1
                    ? templateList[0].id
                    : Number(this.state.selectedTemplate)
                }
                fieldValues={this.state.fieldValues}
                parentId={this.props.task.id}
              />
            ) : (
              <>
                {this.state.loading && (
                  <div className="progress">
                    <div className="indeterminate" />
                  </div>
                )}
                <SubtaskList
                  parent={task}
                  tasks={tasks.data}
                  allSubtasks={allSubtasks}
                  thisUser={thisUser}
                  companyId={companyId}
                  windowSize={windowSize}
                  project={project}
                  onUpdateTask={onUpdateTask}
                  requireTasks={
                    this
                      .handleRequireTasks /*currently not working as the list does noe have scroll and we load only first 100 tasks*/
                  }
                  showProgress={this.state.showProgress}
                  showWeight={this.state.showProgress}
                  allowCreate={
                    canEditSubtasks &&
                    task.haveSubTask &&
                    (!task.draft || !task.preventSubTaskCreationWhenDraft) &&
                    Object.keys(templates).length > 0
                  }
                  canEditSubTasks={canEditSubtasks}
                  members={members}
                  availableTemplates={templates}
                  filteredTasks={filteredTasks}
                  onClickOnTask={handleClickOnTask}
                  companyOwnerId={companyOwnerId}
                  allTasksFetched={this.state.allTasksFetched}
                  onUpdateAssignment={onUpdateAssignment}
                  selectedTemplate={this.state.selectedTemplate}
                  updateTask={updateTask}
                  selectedSubTaskId={this.props.selectedSubTaskId}
                />
              </>
            )}
          </React.Fragment>
        ) : (
          <p>{messages.thereIsNoSubtask(task.subTasksTitle)}</p>
        )}
      </div>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const tasks = state.tasks;
  const users = state.users.map;
  const projects = state.companies.data[props.companyId!]?.projects || [];
  const thisUser = state.thisUser;
  const companyOwnerId =
    state.companies.data[props.companyId!]?.owner?.userId || -1;
  const company = state.companies.data[props.companyId!];

  return { tasks, thisUser, companyOwnerId, users, projects, company };
};

const mapDispatchToProps: MapDispatchToProps<IDispatchToProps, IOwnProps> = (
  dispatch: ThunkDispatch<any, never, any>
) => {
  return {
    searchTasks: (params: ISearchParams) =>
      dispatch(searchTasks({ params })).unwrap(),
    updateTask: (task: ITask) => appDispatch(updateTask({ task })).unwrap(),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SubtaskListContainer);
